import { Button } from "@/components/ui/button";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { cn } from "@/lib/utils";
import eventStore, { setOpenedEvent } from "@/stores/eventStore";
import { useStore } from "@tanstack/react-store";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import getClassNameFromUserAcceptedStatus from "../getEventClassName";
import EventContent from "./content/EventContent";
import EventEditForm from "./form/EventEditForm";

type EventModalProps = {
    editionEnabled?: boolean;
};

const EventModal = ({ editionEnabled = false }: EventModalProps) => {
    const event = useStore(eventStore, (state) => state.openedEvent);

    const { data: user } = useGetUserProfile();

    const [isEditing, setIsEditing] = useState(event?.isNewEvent() ?? false);
    const [updatedEvent, setUpdatedEvent] = useState<AgendaItem>(
        event as AgendaItem,
    );

    useEffect(() => {
        setIsEditing(event?.isNewEvent() ?? false);
        setUpdatedEvent(event as AgendaItem);
    }, [event]);

    if (!updatedEvent || !user) {
        return null;
    }

    const userAcceptedStatus = updatedEvent.getUserAcceptedStatus(user);

    const modalContent = (
        <div
            className={cn(
                "ignore-click-outside event-modal bg-surface-calendarEvent absolute left-1/2 top-1/2 z-overlay w-[500px] max-w-full -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg bg-white text-gray-800 shadow-lg transition-colors duration-500 [&_a]:text-blue-700 [&_a]:underline",
                getClassNameFromUserAcceptedStatus(userAcceptedStatus),
                isEditing &&
                    "fixed w-full max-w-[800px] transition-all duration-500",
            )}
        >
            <Button
                className="absolute right-1 top-1 z-overlay"
                onClick={() => {
                    setOpenedEvent(undefined);
                }}
                variant="invisible"
            >
                <X className="h-4 w-4" />
            </Button>
            {isEditing ? (
                <EventEditForm
                    event={updatedEvent}
                    onSuccess={() => {
                        setOpenedEvent(undefined);
                    }}
                    setIsEditing={setIsEditing}
                />
            ) : (
                <EventContent
                    editionEnabled={editionEnabled}
                    setIsEditing={setIsEditing}
                    setUpdatedEvent={setUpdatedEvent}
                    updatedEvent={updatedEvent}
                />
            )}
        </div>
    );

    // Si on est en mode édition, on utilise un portal pour rendre la modale au niveau du body
    if (isEditing) {
        return createPortal(modalContent, document.body);
    }

    return modalContent;
};

export default EventModal;
